/**
 * Datadog Real User Monitoring for the signed-in app.
 *
 * Off unless the build carries both `VITE_DD_RUM_APPLICATION_ID` and
 * `VITE_DD_RUM_CLIENT_TOKEN` (set per environment in the Fly deploy
 * workflows), so local dev and tests never load the SDK's network paths.
 *
 * What it adds on top of Sentry: page-load and route-change performance
 * (Core Web Vitals), resource and long-task timing, and — because API calls
 * to our own origin carry Datadog trace headers — a click-through from a
 * slow screen to the exact backend trace that served it.
 *
 * Privacy posture mirrors the Sentry setup: `defaultPrivacyLevel: "mask"`
 * for any replay, no replay by default in production, query strings stripped
 * from every URL the SDK reports (OAuth callbacks put codes there), and the
 * user is identified by id only.
 */

import { datadogRum, type RumInitConfiguration } from "@datadog/browser-rum";

let initialized = false;

export const DATADOG_RUM_SERVICE = "yagni-frontend";

function resolveRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return fallback;
  return parsed;
}

/** Drop the query string and fragment: tokens and OAuth codes live there. */
export function stripUrlSecrets(url: string | undefined): string | undefined {
  if (!url) return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

export interface DatadogRumEnv {
  VITE_DD_RUM_APPLICATION_ID?: string;
  VITE_DD_RUM_CLIENT_TOKEN?: string;
  VITE_DD_SITE?: string;
  VITE_DD_ENV?: string;
  VITE_DD_VERSION?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  VITE_DD_RUM_SESSION_SAMPLE_RATE?: string;
  VITE_DD_RUM_REPLAY_SAMPLE_RATE?: string;
  MODE?: string;
}

export function buildDatadogRumConfig(
  env: DatadogRumEnv,
  origin: string,
): RumInitConfiguration | null {
  const applicationId = env.VITE_DD_RUM_APPLICATION_ID?.trim();
  const clientToken = env.VITE_DD_RUM_CLIENT_TOKEN?.trim();
  if (!applicationId || !clientToken) return null;

  const environment = env.VITE_DD_ENV?.trim() || env.VITE_SENTRY_ENVIRONMENT?.trim() || env.MODE || "development";
  const version = env.VITE_DD_VERSION?.trim() || env.VITE_SENTRY_RELEASE?.trim() || undefined;
  const isProd = environment === "production";

  return {
    applicationId,
    clientToken,
    site: (env.VITE_DD_SITE?.trim() || "datadoghq.com") as RumInitConfiguration["site"],
    service: DATADOG_RUM_SERVICE,
    env: environment,
    ...(version ? { version } : {}),
    sessionSampleRate: resolveRate(env.VITE_DD_RUM_SESSION_SAMPLE_RATE, 100),
    // Sentry Replay already records masked sessions; Datadog replay stays
    // off in production unless an operator turns it on for a rollout.
    sessionReplaySampleRate: resolveRate(env.VITE_DD_RUM_REPLAY_SAMPLE_RATE, isProd ? 0 : 20),
    defaultPrivacyLevel: "mask",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Same-origin API calls carry trace headers so a RUM view links to the
    // backend trace that served it. Both propagators: `datadog` for the
    // dd-trace server, `tracecontext` so Sentry's OpenTelemetry layer can
    // read the same parent.
    allowedTracingUrls: [{ match: origin, propagatorTypes: ["datadog", "tracecontext"] }],
    beforeSend(event) {
      event.view.url = stripUrlSecrets(event.view.url) ?? event.view.url;
      if (event.view.referrer) event.view.referrer = stripUrlSecrets(event.view.referrer);
      if (event.type === "resource") {
        event.resource.url = stripUrlSecrets(event.resource.url) ?? event.resource.url;
      }
      return true;
    },
  };
}

export function initDatadogRum(): boolean {
  if (initialized) return true;
  if (typeof window === "undefined") return false;
  const config = buildDatadogRumConfig(import.meta.env as DatadogRumEnv, window.location.origin);
  if (!config) return false;
  try {
    datadogRum.init(config);
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

/** Identify the RUM session by user id only (no email, no name). */
export function setDatadogRumUser(user: { id: string; workspaceId?: string } | null): void {
  if (!initialized) return;
  try {
    if (user) {
      datadogRum.setUser({ id: user.id });
      if (user.workspaceId) datadogRum.setGlobalContextProperty("workspace_id", user.workspaceId);
    } else {
      datadogRum.clearUser();
      datadogRum.removeGlobalContextProperty("workspace_id");
    }
  } catch {
    // telemetry never breaks the app
  }
}

export function isDatadogRumInitialized(): boolean {
  return initialized;
}

export function resetDatadogRumForTests(): void {
  initialized = false;
}
